import { Fragment, useCallback, useEffect, useState, type MouseEvent } from "react";
import {
  Avatar,
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type { AllocationCombinationPair, StatusBreakdown } from "../../../types/userAllocations";
import { getColorForName, getInitials } from "../../../utils/noteUtils";
import {
  combinationPairRowKey,
  formatCount,
  selectionFromStatusRow,
  type CombinationSelection,
} from "./combinationUtils";

const PIPELINE_ROW_HOVER_COLOR = "#e1efea";

const formatGbp = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

function statusRowKey(row: StatusBreakdown) {
  return `${row.id ?? "null"}-${row.name}-${row.sort_order}`;
}

function AssigneeAvatar({ name }: { name: string | null }) {
  if (!name) {
    return (
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>—</Typography>
    );
  }

  return (
    <Tooltip title={name} arrow>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: "0.65rem",
          fontWeight: 700,
          bgcolor: getColorForName(name),
          flexShrink: 0,
        }}
      >
        {getInitials(name)}
      </Avatar>
    </Tooltip>
  );
}

function AssigneeCell({ name, unassignedLabel }: { name: string | null; unassignedLabel: string }) {
  if (!name) {
    return (
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        {unassignedLabel}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <AssigneeAvatar name={name} />
      <Typography
        sx={{
          fontSize: 13,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

type TeamPairingsByStatusTableProps = {
  pairs: AllocationCombinationPair[];
  onSelectCombination: (selection: CombinationSelection) => void;
};

export function TeamPairingsByStatusTable({
  pairs,
  onSelectCombination,
}: TeamPairingsByStatusTableProps) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setExpandedKeys(new Set());
  }, [pairs]);

  const togglePair = useCallback((pair: AllocationCombinationPair) => {
    const key = combinationPairRowKey(pair);
    const hasStatusRows = (pair.by_status ?? []).some((s) => s.deal_count > 0);
    if (!hasStatusRows) return;

    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleStatusClick = useCallback(
    (
      pair: AllocationCombinationPair,
      status: StatusBreakdown,
      event: MouseEvent,
    ) => {
      event.stopPropagation();
      if (status.deal_count <= 0) return;
      onSelectCombination(
        selectionFromStatusRow({
          lead_user_id: pair.lead_user_id,
          lead_name: pair.lead_name,
          support_user_id: pair.support_user_id,
          support_name: pair.support_name,
          status,
          pair,
        }),
      );
    },
    [onSelectCombination],
  );

  return (
    <TableContainer>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36, px: 0.5 }} aria-hidden />
              <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#182737" }}>
                Lead
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#182737" }}>
                Support
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 600, fontSize: 12, color: "#182737", width: 64 }}
              >
                Deals
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 600, fontSize: 12, color: "#182737", width: 110 }}
              >
                Volume (£)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pairs.map((pair) => {
              const pairKey = combinationPairRowKey(pair);
              const isExpanded = expandedKeys.has(pairKey);
              const statusRows = (pair.by_status ?? [])
                .filter((s) => s.deal_count > 0)
                .slice()
                .sort((a, b) => a.sort_order - b.sort_order);
              const canExpand = statusRows.length > 0;

              return (
                <Fragment key={pairKey}>
                  <TableRow
                    hover={canExpand}
                    onClick={() => togglePair(pair)}
                    sx={{
                      cursor: canExpand ? "pointer" : "default",
                      "&:hover": canExpand
                        ? { backgroundColor: PIPELINE_ROW_HOVER_COLOR }
                        : undefined,
                      "& > *": {
                        borderBottom: isExpanded ? "none" : undefined,
                      },
                    }}
                  >
                    <TableCell sx={{ px: 0.5, py: 0.75 }}>
                      {canExpand ? (
                        isExpanded ? (
                          <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        ) : (
                          <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        )
                      ) : (
                        <Box sx={{ width: 20 }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <AssigneeCell
                        name={pair.lead_name}
                        unassignedLabel="Unassigned lead"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      <AssigneeCell
                        name={pair.support_name}
                        unassignedLabel="Unassigned support"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, py: 0.75 }}>
                      {formatCount(pair.deal_count)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, py: 0.75 }}>
                      {formatGbp(pair.loan_amount_gbp)}
                    </TableCell>
                  </TableRow>
                  {canExpand && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ py: 0, px: 0, borderBottom: isExpanded ? undefined : "none" }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 1.5, py: 1, bgcolor: "#f8f9fa" }}>
                            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12, pl: 3 }}>
                                    Status
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, fontSize: 12, width: 64 }}
                                  >
                                    Deals
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, fontSize: 12, width: 110 }}
                                  >
                                    Volume (£)
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {statusRows.map((status) => (
                                  <TableRow
                                    key={statusRowKey(status)}
                                    hover
                                    onClick={(event) =>
                                      handleStatusClick(pair, status, event)
                                    }
                                    sx={{
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: PIPELINE_ROW_HOVER_COLOR,
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ fontSize: 13, pl: 3 }}>
                                      {status.name}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: 13 }}>
                                      {formatCount(status.deal_count)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: 13 }}>
                                      {formatGbp(status.loan_amount_gbp)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
  );
}
